import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Inputs } from 'components/form-control';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import { UPDATE_CONCIERGE_BODY } from 'models';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { getConfigConciergeApi, updateConfigConciergeApi } from 'services';
import { setGlobalLoading } from 'store/reducers/global';

interface IFormInputs {
  sitter_rate: number;
  sitter_search_fee: number;
  ac_trial_job_fee: number;
}

const defaultValues: Partial<IFormInputs> = {
  sitter_rate: 0,
  sitter_search_fee: 0,
  ac_trial_job_fee: 0,
};

const Concierge = () => {
  const schema = useMemo(() => {
    return yup.object({
      sitter_rate: yup.number().min(0).required('Sitter rate is required'),
      sitter_search_fee: yup
        .number()
        .min(0)
        .required('Sitter search fee is required'),
      ac_trial_job_fee: yup
        .number()
        .min(0)
        .required('AC trial job fee is required'),
    });
  }, []);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const { reset, handleSubmit } = form;

  const fetchData = async () => {
    setGlobalLoading(true);
    await getConfigConciergeApi()
      .then(({ data: { data } }) => {
        reset({
          sitter_rate: data.sitter_rate,
          sitter_search_fee: data.sitter_search_fee,
          ac_trial_job_fee: data.ac_trial_job_fee,
        });
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setGlobalLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (value: IFormInputs) => {
    const data: UPDATE_CONCIERGE_BODY = {
      sitter_rate: value.sitter_rate,
      sitter_search_fee: value.sitter_search_fee,
      ac_trial_job_fee: value.ac_trial_job_fee,
    };
    setGlobalLoading(true);
    await updateConfigConciergeApi(data)
      .then((r) => {
        fetchData();
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setGlobalLoading(false);
      });
  };

  return (
    <Box onSubmit={handleSubmit(onSubmit)} component="form">
      <Stack sx={{ width: '475px' }} spacing="16px">
        <Stack>
          <Inputs
            type="number"
            title="Search Fee"
            form={form}
            name="sitter_search_fee"
          />
          <Typography
            variant="Caption_12_Regular"
            color="rgba(142, 142, 147, 1)"
          >
            Search fee for AC request
          </Typography>
        </Stack>
        <Stack>
          <Inputs
            type="number"
            title="Trial Job Charge (parent rate per hour)"
            form={form}
            name="ac_trial_job_fee"
          />
          <Typography
            variant="Caption_12_Regular"
            color="rgba(142, 142, 147, 1)"
          >
            AC parent rate per hour
          </Typography>
        </Stack>
        <Stack>
          <Inputs
            title="Trial Job Fee (sitter rate per hour)"
            form={form}
            name="sitter_rate"
            type="number"
          />
          <Typography
            variant="Caption_12_Regular"
            color="rgba(142, 142, 147, 1)"
          >
            AC sitter rate per hour
          </Typography>
        </Stack>
        <Divider />
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          sx={{
            height: '30px',
          }}
          type="submit"
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
};

export default Concierge;
